import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EmptyState,
  ErrorPanel,
  InfoCard,
  MarkdownContent,
  Progress,
} from '@backstage/core-components';
import {
  useApi,
  useRouteRef,
  alertApiRef,
  errorApiRef,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import {
  ANNOTATION_EDIT_URL,
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Direction } from '@backstage/plugin-catalog-graph';
import type { EntityNode } from '@backstage/plugin-catalog-graph';
import type { DependencyGraphTypes } from '@backstage/core-components';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SvgIcon from '@material-ui/core/SvgIcon';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import CachedIcon from '@material-ui/icons/Cached';
import { Alert } from '@material-ui/lab';
import { useAsyncRetry } from 'react-use';
import classNames from 'classnames';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ParentGroupDetails,
  deriveParentGroup,
} from '../../utils/parentGrouping';
import { GroupedEntityRelationsGraph } from '../Graph/GroupedEntityRelationsGraph';

type ActorSpec =
  | string
  | {
      name: string;
      description?: string;
    };

interface StorySpec {
  owner?: string | string[];
  system?: string | string[];
  actors?: ActorSpec | ActorSpec[];
  nodes?: string | string[];
  description?: string;
  objective?: string;
  businessflow?: string;
  businessFlow?: string;
  acceptanceCriteria?:
    | string
    | AcceptanceCriteriaSpec
    | (string | AcceptanceCriteriaSpec)[];
  [key: string]: unknown;
}

type AcceptanceCriteriaSpec = {
  id?: string;
  scenarioId?: string;
  criteria?: string;
  summary?: string;
  scenario?: string;
  type?: string;
  actor?: string | string[];
  actors?: string | string[];
  description?: string;
  given?: string;
  when?: string;
  and_when?: string | string[];
  andWhen?: string | string[];
  then?: string;
  and_then?: string | string[];
  andThen?: string | string[];
  than?: string;
};

interface NormalizedActor {
  name: string;
  description?: string;
}

interface NormalizedStory {
  entity: Entity;
  ref: string;
  displayName: string;
  description?: string;
  owner: string[];
  actors: NormalizedActor[];
  nodes: string[];
  objective?: string;
  businessFlow?: string;
  acceptanceCriteria: NormalizedAcceptanceCriterion[];
}

type StoryStep = {
  ref: string;
  label: string;
  isStart: boolean;
  isActive: boolean;
  parentGroup?: ParentGroupDetails;
};

type NormalizedAcceptanceCriterion = {
  id?: string;
  summary?: string;
  details?: string;
  type?: string;
  actors: string[];
  given?: string;
  when?: string;
  andWhen?: string[];
  then?: string;
  andThen?: string[];
};

type EntityNodeRenderProps = DependencyGraphTypes.RenderNodeProps<EntityNode>;

type UserStoryDefaultRenderNodeProps = EntityNodeRenderProps & {
  parentGroup?: ParentGroupDetails;
};

const useGraphNodeStyles = makeStyles(
  theme => ({
    node: {
      fill: theme.palette.grey[300],
      stroke: theme.palette.grey[300],
      '&.primary': {
        fill: theme.palette.primary.light,
        stroke: theme.palette.primary.light,
      },
      '&.secondary': {
        fill: theme.palette.secondary.light,
        stroke: theme.palette.secondary.light,
      },
    },
    text: {
      fill: theme.palette.getContrastText(theme.palette.grey[300]),
      '&.primary': {
        fill: theme.palette.primary.contrastText,
      },
      '&.secondary': {
        fill: theme.palette.secondary.contrastText,
      },
      '&.focused': {
        fontWeight: 'bold',
      },
    },
    clickable: {
      cursor: 'pointer',
    },
  }),
  { name: 'UserStoryGraphDefaultNode' },
);

const UserStoryDefaultRenderNode = ({
  node,
  parentGroup: _parentGroup,
}: UserStoryDefaultRenderNodeProps) => {
  const classes = useGraphNodeStyles();
  const { id, entity, color = 'default', focused, onClick } = node;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const textRef = useRef<SVGTextElement | null>(null);
  const presentation = useEntityPresentation(entity, {
    defaultNamespace: DEFAULT_NAMESPACE,
  });

  useLayoutEffect(() => {
    if (!textRef.current) {
      return;
    }

    const { width: measuredWidth, height: measuredHeight } =
      textRef.current.getBBox();
    const roundedWidth = Math.round(measuredWidth);
    const roundedHeight = Math.round(measuredHeight);

    if (roundedWidth !== width || roundedHeight !== height) {
      setWidth(roundedWidth);
      setHeight(roundedHeight);
    }
  }, [height, width]);

  const hasKindIcon = Boolean(presentation.Icon);
  const padding = 10;
  const iconSize = height;
  const paddedIconWidth = hasKindIcon ? iconSize + padding : 0;
  const paddedWidth = paddedIconWidth + width + padding * 2;
  const paddedHeight = height + padding * 2;
  const displayTitle = presentation.primaryTitle ?? id;
  const IconComponent = (presentation.Icon ?? SvgIcon) as React.ComponentType<
    React.ComponentProps<typeof SvgIcon>
  >;

  return (
    <g
      onClick={onClick}
      className={classNames(onClick && classes.clickable)}
      role={onClick ? 'button' : undefined}
    >
      <rect
        className={classNames(
          classes.node,
          color === 'primary' && 'primary',
          color === 'secondary' && 'secondary',
        )}
        width={paddedWidth}
        height={paddedHeight}
        rx={10}
      />
      {hasKindIcon ? (
        <IconComponent
          y={padding}
          x={padding}
          width={iconSize}
          height={iconSize}
          className={classNames(
            classes.text,
            focused && 'focused',
            color === 'primary' && 'primary',
            color === 'secondary' && 'secondary',
          )}
        />
      ) : null}
      <text
        ref={textRef}
        className={classNames(
          classes.text,
          focused && 'focused',
          color === 'primary' && 'primary',
          color === 'secondary' && 'secondary',
        )}
        y={paddedHeight / 2}
        x={paddedIconWidth + (width + padding * 2) / 2}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {displayTitle}
      </text>
      <title>{presentation.entityRef}</title>
    </g>
  );
};

const useStyles = makeStyles(theme => ({
  sidebarPaper: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  filterChip: {
    alignSelf: 'flex-start',
  },
  storyList: {
    overflowY: 'auto',
    flex: 1,
  },
  contentPaper: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  chipGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  graphWrapper: {
    flex: 1,
    minHeight: 480,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    height: '100%',
    '& > *': {
      width: '100%',
      height: '100%',
    },
  },
  graphContainer: {
    minHeight: 480,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: theme.spacing(2),
    '& > *': {
      width: '100%',
      height: '100%',
    },
  },
  flowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flexGrow: 1,
  },
  flowStepRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  flowStepTrigger: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    outline: 'none',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    '&:focus': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
  },
  flowStepSwatch: {
    width: 12,
    height: 12,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[400],
    flexShrink: 0,
  },
  actorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flexGrow: 1,
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  detailHeaderContent: {
    flex: 1,
    minWidth: 0,
  },
  detailHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  acceptanceCriteriaList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  acceptanceCriteriaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  acceptanceCriteriaDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  acceptanceCriteriaMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  acceptanceCriteriaMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  acceptanceCriteriaTypeChip: {
    textTransform: 'capitalize',
    fontWeight: 600,
  },
  acceptanceCriteriaTypePositive: {
    color: theme.palette.success.contrastText,
    backgroundColor: theme.palette.success.light,
  },
  acceptanceCriteriaTypeNegative: {
    color: theme.palette.error.contrastText,
    backgroundColor: theme.palette.error.light,
  },
  acceptanceCriteriaActors: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  acceptanceCriteriaClause: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  acceptanceCriteriaClauseLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: '0.75rem',
    color: theme.palette.text.primary,
  },
}));

const toArray = (value?: string | string[]): string[] => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const normalizeEntityRef = (
  value: string,
  defaults: { defaultKind: string; defaultNamespace: string },
): string => {
  try {
    const { kind, namespace, name } = parseEntityRef(value, defaults);
    return stringifyEntityRef({ kind, namespace, name });
  } catch (error) {
    return value;
  }
};

const formatRefLabel = (ref: string): string => {
  try {
    const { namespace, name } = parseEntityRef(ref);
    const namespaceSuffix =
      namespace && namespace !== 'default' ? ` (${namespace})` : '';
    return `${name}${namespaceSuffix}`;
  } catch (error) {
    return ref;
  }
};

const normalizeActors = (input: StorySpec['actors']): NormalizedActor[] => {
  if (!input) {
    return [];
  }

  const values = Array.isArray(input) ? input : [input];

  return values
    .map(value => {
      if (!value) {
        return undefined;
      }

      if (typeof value === 'string') {
        return { name: value };
      }

      if (typeof value === 'object' && typeof value.name === 'string') {
        const actor: NormalizedActor = {
          name: value.name,
        };

        if (typeof value.description === 'string' && value.description.trim()) {
          actor.description = value.description;
        }

        return actor;
      }

      return undefined;
    })
    .filter((actor): actor is NormalizedActor => Boolean(actor));
};

const normalizeStory = (entity: Entity): NormalizedStory => {
  const spec = (entity.spec ?? {}) as StorySpec;
  const namespace = entity.metadata.namespace ?? 'default';
  const description =
    typeof spec.description === 'string'
      ? spec.description
      : entity.metadata.description;

  const nodes = toArray(spec.nodes).map(node =>
    normalizeEntityRef(node, {
      defaultKind: 'Component',
      defaultNamespace: namespace,
    }),
  );

  return {
    entity,
    ref: stringifyEntityRef(entity),
    displayName: entity.metadata.title ?? entity.metadata.name,
    description,
    owner: toArray(spec.owner),
    actors: normalizeActors(spec.actors),
    nodes,
    objective: normalizeRichText(spec.objective),
    businessFlow: normalizeRichText(spec.businessFlow ?? spec.businessflow),
    acceptanceCriteria: normalizeAcceptanceCriteria(spec.acceptanceCriteria),
  };
};

const normalizeRichText = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const normalizeStringArray = (value?: string | string[]): string[] => {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];
  return values
    .map(entry => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry): entry is string => Boolean(entry));
};

const normalizeAcceptanceCriteria = (
  input: StorySpec['acceptanceCriteria'],
): NormalizedAcceptanceCriterion[] => {
  if (!input) {
    return [];
  }

  const values = Array.isArray(input) ? input : [input];

  return values
    .map(value => {
      if (!value) {
        return undefined;
      }

      if (typeof value === 'string') {
        const summary = value.trim();

        if (!summary) {
          return undefined;
        }

        return { summary, actors: [] } as NormalizedAcceptanceCriterion;
      }

      if (typeof value === 'object') {
        const criterion: NormalizedAcceptanceCriterion = {
          actors: [],
        };

        const rawSummary =
          value.scenario ??
          value.criteria ??
          value.summary ??
          value.description;
        const normalizedSummary = normalizeRichText(rawSummary);
        const normalizedDetails = normalizeRichText(value.description);

        const scenarioId = normalizeRichText(
          (value as { id?: string; scenarioId?: string }).id ??
            value.scenarioId,
        );
        if (scenarioId) {
          criterion.id = scenarioId;
        }

        if (normalizedSummary) {
          criterion.summary = normalizedSummary;
          if (normalizedDetails && normalizedDetails !== normalizedSummary) {
            criterion.details = normalizedDetails;
          }
        } else if (normalizedDetails) {
          criterion.summary = normalizedDetails;
        }

        const type = normalizeRichText(value.type);
        if (type) {
          criterion.type = type;
        }

        const actors = normalizeStringArray(value.actor ?? value.actors);
        if (actors.length) {
          criterion.actors = actors;
        }

        const given = normalizeRichText(value.given);
        if (given) {
          criterion.given = given;
        }

        const when = normalizeRichText(value.when);
        if (when) {
          criterion.when = when;
        }

        const andWhenValues = normalizeStringArray(
          value.and_when ?? value.andWhen,
        );
        if (andWhenValues.length) {
          criterion.andWhen = andWhenValues;
        }

        const then = normalizeRichText(value.then ?? value.than);
        if (then) {
          criterion.then = then;
        }

        const andThenValues = normalizeStringArray(
          value.and_then ?? value.andThen,
        );
        if (andThenValues.length) {
          criterion.andThen = andThenValues;
        }

        if (!criterion.actors) {
          criterion.actors = [];
        }

        if (
          criterion.summary ||
          criterion.details ||
          criterion.type ||
          criterion.actors.length ||
          criterion.given ||
          criterion.when ||
          criterion.andWhen?.length ||
          criterion.then ||
          criterion.andThen?.length
        ) {
          return criterion;
        }
      }

      return undefined;
    })
    .filter((criterion): criterion is NormalizedAcceptanceCriterion =>
      Boolean(criterion),
    );
};

export const UserStoriesContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const navigate = useNavigate();
  const systemRef = stringifyEntityRef(entity);

  const [filter, setFilter] = useState('');
  const [nodeFilterRef, setNodeFilterRef] = useState<string | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedStoryRef = searchParams.get('story') || undefined;

  const setSelectedStoryRef = useCallback(
    (ref: string | undefined) => {
      if (ref) {
        setSearchParams({ story: ref }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams],
  );
  const [refreshingStoryRef, setRefreshingStoryRef] = useState<
    string | undefined
  >();

  const toggleNodeFilter = useCallback((ref: string) => {
    setNodeFilterRef(previous => (previous === ref ? undefined : ref));
  }, []);

  const clearNodeFilter = useCallback(() => {
    setNodeFilterRef(undefined);
  }, []);

  const handleGraphNodeClick = useCallback(
    (node: EntityNode, event?: React.MouseEvent<unknown>) => {
      const params = entityRouteParams(node.entity);
      const target = catalogEntityRoute(params);

      if (event) {
        event.preventDefault();
        event.stopPropagation();

        if (event.metaKey || event.ctrlKey || event.button === 1) {
          window.open(target, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      navigate(target);
    },
    [catalogEntityRoute, navigate],
  );

  const { value, loading, error } = useAsyncRetry(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'Story',
        'relations.partOf': systemRef,
      },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.description',
        'metadata.annotations',
        'spec.owner',
        'spec.actors',
        'spec.nodes',
        'spec.description',
        'spec.objective',
        'spec.businessflow',
        'spec.businessFlow',
        'spec.acceptanceCriteria',
      ],
    });

    return response;
  }, [catalogApi, systemRef]);

  const stories = useMemo(() => {
    const items = value?.items ?? [];
    return items
      .map(normalizeStory)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [value]);

  const filteredStories = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase('en-US');
    const normalizedFilter = nodeFilterRef?.toLocaleLowerCase('en-US');

    return stories.filter(story => {
      if (normalizedFilter) {
        const hasNode = story.nodes
          .map(ref => ref.toLocaleLowerCase('en-US'))
          .includes(normalizedFilter);
        if (!hasNode) {
          return false;
        }
      }

      if (!query) {
        return true;
      }

      const actorFields = story.actors.flatMap(actor => [
        actor.name,
        actor.description ?? '',
      ]);
      const searchable = [
        story.displayName,
        story.description ?? '',
        ...actorFields,
        ...story.owner,
      ]
        .join(' ')
        .toLocaleLowerCase('en-US');
      return searchable.includes(query);
    });
  }, [stories, filter, nodeFilterRef]);

  useEffect(() => {
    if (!filteredStories.length) {
      if (selectedStoryRef) {
        setSelectedStoryRef(undefined);
      }
      return;
    }

    const stillExists = filteredStories.some(
      story => story.ref === selectedStoryRef,
    );
    if (!stillExists) {
      const nextRef = filteredStories[0].ref;
      if (nextRef !== selectedStoryRef) {
        setSelectedStoryRef(nextRef);
      }
    }
  }, [filteredStories, selectedStoryRef]);

  const selectedStory = filteredStories.find(
    story => story.ref === selectedStoryRef,
  );

  const selectedStoryEditUrl =
    selectedStory?.entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const isSchedulingStoryRefresh = Boolean(
    selectedStory && refreshingStoryRef === selectedStory.ref,
  );

  const handleScheduleStoryRefresh = useCallback(async () => {
    if (!selectedStory) {
      return;
    }

    try {
      setRefreshingStoryRef(selectedStory.ref);
      await catalogApi.refreshEntity(selectedStory.ref);
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (refreshError) {
      errorApi.post(refreshError as Error);
    } finally {
      setRefreshingStoryRef(undefined);
    }
  }, [alertApi, catalogApi, errorApi, selectedStory]);

  const storyNodeRefsKey = useMemo(
    () =>
      (selectedStory?.nodes ?? [])
        .map(ref => ref.toLocaleLowerCase('en-US'))
        .join('|'),
    [selectedStory],
  );

  const { value: nodeEntitiesResponse } = useAsyncRetry(async () => {
    if (!selectedStory?.nodes?.length) {
      return undefined;
    }

    return catalogApi.getEntitiesByRefs({
      entityRefs: selectedStory.nodes,
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'relations',
      ],
    });
  }, [catalogApi, selectedStory, storyNodeRefsKey]);

  const nodeEntityMap = useMemo(() => {
    const map = new Map<string, Entity>();
    nodeEntitiesResponse?.items?.forEach(item => {
      if (!item) {
        return;
      }
      const normalized = stringifyEntityRef(item).toLocaleLowerCase('en-US');
      map.set(normalized, item);
    });
    return map;
  }, [nodeEntitiesResponse]);

  const parentGroupMap = useMemo(() => {
    const map = new Map<string, ParentGroupDetails>();
    nodeEntityMap.forEach((entityItem, ref) => {
      const details = deriveParentGroup(entityItem);
      if (details) {
        map.set(ref, details);
      }
    });
    return map;
  }, [nodeEntityMap]);

  const resolveParentGroup = useCallback(
    (entityItem: Entity): ParentGroupDetails | undefined => {
      const normalized =
        stringifyEntityRef(entityItem).toLocaleLowerCase('en-US');
      return parentGroupMap.get(normalized) ?? deriveParentGroup(entityItem);
    },
    [parentGroupMap],
  );

  const renderGraphNode = useCallback(
    ({ node: graphNode }: EntityNodeRenderProps) => (
      <UserStoryDefaultRenderNode
        node={graphNode}
        parentGroup={resolveParentGroup(graphNode.entity)}
      />
    ),
    [resolveParentGroup],
  );

  const normalizedStoryNodeRefs = useMemo(() => {
    const refs = selectedStory?.nodes ?? [];
    return refs.map(ref => ref.toLocaleLowerCase('en-US'));
  }, [selectedStory]);

  const normalizedNodeFilterRef = useMemo(
    () => nodeFilterRef?.toLocaleLowerCase('en-US'),
    [nodeFilterRef],
  );

  const storySteps = useMemo<StoryStep[]>(() => {
    if (!selectedStory) {
      return [];
    }

    return selectedStory.nodes.map((ref, index) => {
      const isStart = index === 0;
      const normalizedRef = ref.toLocaleLowerCase('en-US');
      return {
        ref,
        label: isStart ? `Start · ${formatRefLabel(ref)}` : formatRefLabel(ref),
        isStart,
        isActive: normalizedNodeFilterRef
          ? normalizedNodeFilterRef === normalizedRef
          : false,
        parentGroup: parentGroupMap.get(normalizedRef),
      };
    });
  }, [selectedStory, normalizedNodeFilterRef, parentGroupMap]);

  const allowedNodeRefs = useMemo(
    () => new Set(normalizedStoryNodeRefs),
    [normalizedStoryNodeRefs],
  );

  const allowedNodeNames = useMemo(() => {
    const refs = selectedStory?.nodes ?? [];
    const names = refs
      .map(ref => {
        try {
          return parseEntityRef(ref).name.toLocaleLowerCase('en-US');
        } catch (error) {
          return undefined;
        }
      })
      .filter((value): value is string => Boolean(value));
    return new Set(names);
  }, [selectedStory]);

  const rootEntityRefs = useMemo(() => {
    const refs = selectedStory?.nodes?.slice(0, 1) ?? []; // Only take the first node
    const parsed = refs
      .map(ref => {
        try {
          return parseEntityRef(ref);
        } catch (error) {
          return undefined;
        }
      })
      .filter((value): value is ReturnType<typeof parseEntityRef> =>
        Boolean(value),
      );
    return parsed;
  }, [selectedStory]);

  const entityFilter = useCallback(
    (candidate: Entity) => {
      const candidateRef =
        stringifyEntityRef(candidate).toLocaleLowerCase('en-US');
      const candidateName = candidate.metadata.name?.toLocaleLowerCase('en-US');
      if (candidateName && allowedNodeNames.has(candidateName)) {
        return true;
      }

      return allowedNodeRefs.has(candidateRef);
    },
    [allowedNodeNames, allowedNodeRefs],
  );

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ErrorPanel error={error} />;
  }

  if (!stories.length) {
    return (
      <EmptyState
        missing="content"
        title="No user stories"
        description="Register Story entities for this system to visualise user journeys."
      />
    );
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      <Grid item xs={12} md={4} lg={3}>
        <Paper className={classes.sidebarPaper} elevation={1}>
          <div>
            <Typography variant="h6">User Stories</Typography>
            <Typography variant="body2" color="textSecondary">
              {stories.length} total
            </Typography>
          </div>
          <TextField
            label="Filter"
            placeholder="Search by name, actor, owner"
            variant="outlined"
            size="small"
            value={filter}
            onChange={event => setFilter(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {nodeFilterRef ? (
            <Chip
              className={classes.filterChip}
              color="secondary"
              size="small"
              label={`Node filter · ${formatRefLabel(nodeFilterRef)}`}
              onDelete={clearNodeFilter}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              Click a flow step to filter linked stories. Selecting a graph node
              opens the catalog entity in a new view. Colored headers highlight
              the parent component or system for each node.
            </Typography>
          )}
          <div className={classes.storyList}>
            <List dense disablePadding>
              {filteredStories.map(story => {
                const selected = story.ref === selectedStoryRef;
                const actorNames = story.actors.map(actor => actor.name);
                const secondary = story.actors.length
                  ? `Actors: ${actorNames.join(', ')}`
                  : story.owner.length
                  ? `Owner: ${story.owner.join(', ')}`
                  : undefined;

                return (
                  <Tooltip
                    key={story.ref}
                    title={story.displayName}
                    placement="right"
                  >
                    <ListItem
                      button
                      selected={selected}
                      onClick={() => setSelectedStoryRef(story.ref)}
                    >
                      <ListItemText
                        primary={story.displayName}
                        secondary={secondary}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
            {!filteredStories.length && (
              <Alert severity="info">
                {nodeFilterRef ? (
                  <>
                    No stories include {formatRefLabel(nodeFilterRef)} with the
                    current filters. Clear the node filter to see all stories.
                  </>
                ) : (
                  'No stories match this filter.'
                )}
              </Alert>
            )}
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8} lg={9}>
        {selectedStory ? (
          <>
            <div className={classes.detailHeader} style={{ marginBottom: 20 }}>
              <div className={classes.detailHeaderContent}>
                <Typography variant="h5" gutterBottom>
                  {selectedStory.displayName}
                </Typography>
                <Box className={classes.chipGroup}>
                  {selectedStory.actors.map(actor => (
                    <Chip key={actor.name} label={actor.name} size="small" />
                  ))}
                  {selectedStory.owner.map(owner => (
                    <Chip key={owner} label={`Owner · ${owner}`} size="small" />
                  ))}
                </Box>
              </div>
              <div className={classes.detailHeaderActions}>
                <Tooltip title="Schedule refresh">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Schedule catalog refresh"
                      onClick={handleScheduleStoryRefresh}
                      disabled={!selectedStory || isSchedulingStoryRefresh}
                    >
                      <CachedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit in source">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Edit story source"
                      component={selectedStoryEditUrl ? 'a' : 'button'}
                      href={selectedStoryEditUrl}
                      target={selectedStoryEditUrl ? '_blank' : undefined}
                      rel={
                        selectedStoryEditUrl ? 'noopener noreferrer' : undefined
                      }
                      disabled={!selectedStoryEditUrl}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box
              style={{
                minHeight: '50px',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 20,
              }}
            >
              <InfoCard title="Dependency Graph">
                {rootEntityRefs.length ? (
                  <GroupedEntityRelationsGraph
                    rootEntityNames={rootEntityRefs}
                    direction={Direction.LEFT_RIGHT}
                    maxDepth={10}
                    relations={['dependencyOf', 'consumesApi']}
                    mergeRelations={false}
                    unidirectional={false}
                    entityFilter={entityFilter}
                    zoom="enabled"
                    showArrowHeads
                    renderNode={renderGraphNode}
                    onNodeClick={handleGraphNodeClick}
                    getParentGroup={deriveParentGroup}
                    groupPadding={36}
                    groupHeaderHeight={30}
                  />
                ) : (
                  <Box className={classes.graphContainer}>
                    <Typography color="textSecondary">
                      No catalog entities match the nodes listed for this story.
                    </Typography>
                  </Box>
                )}
              </InfoCard>
            </Box>

            {selectedStory.description ? (
              <Box
                style={{
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Description" variant="gridItem">
                  <MarkdownContent
                    content={
                      selectedStory.description?.trim() ||
                      '_No description provided for this story._'
                    }
                  />
                </InfoCard>{' '}
              </Box>
            ) : null}

            {selectedStory.objective ? (
              <Box
                style={{
                  minHeight: '50px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Objective">
                  <MarkdownContent
                    content={
                      selectedStory.objective.trim() ||
                      '_No objective provided for this story._'
                    }
                  />
                </InfoCard>
              </Box>
            ) : null}

            {selectedStory.actors.length ? (
              <Box
                style={{
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Actors" variant="gridItem">
                  <div className={classes.actorList}>
                    {selectedStory.actors.map(actor => (
                      <div key={actor.name}>
                        <Typography variant="subtitle1">
                          {actor.name}
                        </Typography>
                        {actor.description ? (
                          <Typography variant="body2" color="textSecondary">
                            {actor.description}
                          </Typography>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </InfoCard>
              </Box>
            ) : null}

            {selectedStory.businessFlow ? (
              <Box
                style={{
                  minHeight: '50px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Business Flow">
                  <MarkdownContent
                    content={
                      selectedStory.businessFlow.trim() ||
                      '_No business flow provided for this story._'
                    }
                  />
                </InfoCard>
              </Box>
            ) : null}

            {storySteps.length ? (
              <Box
                style={{
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Component Flow" variant="gridItem">
                  <div className={classes.flowList}>
                    {storySteps.map((step, index) => (
                      <div
                        key={`${step.ref}-${index}`}
                        className={classes.flowStepRow}
                      >
                        <div
                          className={classes.flowStepTrigger}
                          role="button"
                          tabIndex={0}
                          aria-pressed={step.isActive}
                          onClick={() => toggleNodeFilter(step.ref)}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleNodeFilter(step.ref);
                            }
                          }}
                        >
                          {step.parentGroup ? (
                            <span
                              className={classes.flowStepSwatch}
                              style={{
                                backgroundColor: step.parentGroup.color,
                              }}
                            />
                          ) : null}
                          <Typography
                            color={step.isActive ? 'secondary' : undefined}
                            style={
                              step.isActive ? { fontWeight: 600 } : undefined
                            }
                          >
                            {index + 1}. {step.label}
                          </Typography>
                        </div>
                        {step.parentGroup ? (
                          <Typography variant="caption" color="textSecondary">
                            Parent · {step.parentGroup.label}
                          </Typography>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </InfoCard>
              </Box>
            ) : null}

            {selectedStory.acceptanceCriteria.length ? (
              <Box
                style={{
                  minHeight: '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                }}
              >
                <InfoCard title="Acceptance Criteria" variant="gridItem">
                  <div className={classes.acceptanceCriteriaList}>
                    {selectedStory.acceptanceCriteria.map(
                      (criterion, index) => (
                        <div
                          key={
                            criterion.id ??
                            `${criterion.summary ?? 'criterion'}-${index}`
                          }
                          className={classes.acceptanceCriteriaItem}
                        >
                          {criterion.summary ? (
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 700 }}
                            >
                              {criterion.summary}
                            </Typography>
                          ) : (
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 700 }}
                            >
                              {`Criteria ${index + 1}`}
                            </Typography>
                          )}
                          {criterion.details ? (
                            <Typography variant="body2" color="textSecondary">
                              {criterion.details}
                            </Typography>
                          ) : null}
                          {criterion.id ||
                          criterion.type ||
                          criterion.actors.length ? (
                            <div className={classes.acceptanceCriteriaMetaRow}>
                              {criterion.id ? (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Scenario ID:
                                  </Typography>
                                  <Chip label={criterion.id} size="small" />
                                </>
                              ) : null}
                              {criterion.type ? (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Type:
                                  </Typography>
                                  <Chip
                                    label={criterion.type}
                                    size="small"
                                    className={classNames(
                                      classes.acceptanceCriteriaTypeChip,
                                      criterion.type.toLocaleLowerCase(
                                        'en-US',
                                      ) === 'positive'
                                        ? classes.acceptanceCriteriaTypePositive
                                        : criterion.type.toLocaleLowerCase(
                                            'en-US',
                                          ) === 'negative'
                                        ? classes.acceptanceCriteriaTypeNegative
                                        : undefined,
                                    )}
                                  />
                                </>
                              ) : null}
                              {criterion.actors.length ? (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Actors:
                                  </Typography>
                                  <div
                                    className={classes.acceptanceCriteriaActors}
                                  >
                                    {criterion.actors.map(actor => (
                                      <Chip
                                        key={`${actor}-${index}`}
                                        label={actor}
                                        size="small"
                                      />
                                    ))}
                                  </div>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                          <div className={classes.acceptanceCriteriaDetail}>
                            {criterion.given ? (
                              <Typography
                                variant="body2"
                                className={classes.acceptanceCriteriaClause}
                              >
                                <span
                                  className={
                                    classes.acceptanceCriteriaClauseLabel
                                  }
                                >
                                  Given:
                                </span>
                                {criterion.given}
                              </Typography>
                            ) : null}
                            {criterion.when ? (
                              <Typography
                                variant="body2"
                                className={classes.acceptanceCriteriaClause}
                              >
                                <span
                                  className={
                                    classes.acceptanceCriteriaClauseLabel
                                  }
                                >
                                  When:
                                </span>
                                {criterion.when}
                              </Typography>
                            ) : null}
                            {criterion.andWhen?.map((clause, clauseIndex) => (
                              <Typography
                                variant="body2"
                                className={classes.acceptanceCriteriaClause}
                                key={`and-when-${index}-${clauseIndex}`}
                              >
                                <span
                                  className={
                                    classes.acceptanceCriteriaClauseLabel
                                  }
                                >
                                  And When:
                                </span>
                                {clause}
                              </Typography>
                            ))}
                            {criterion.then ? (
                              <Typography
                                variant="body2"
                                className={classes.acceptanceCriteriaClause}
                              >
                                <span
                                  className={
                                    classes.acceptanceCriteriaClauseLabel
                                  }
                                >
                                  Then:
                                </span>
                                {criterion.then}
                              </Typography>
                            ) : null}
                            {criterion.andThen?.map((clause, clauseIndex) => (
                              <Typography
                                variant="body2"
                                className={classes.acceptanceCriteriaClause}
                                key={`and-then-${index}-${clauseIndex}`}
                              >
                                <span
                                  className={
                                    classes.acceptanceCriteriaClauseLabel
                                  }
                                >
                                  And Then:
                                </span>
                                {clause}
                              </Typography>
                            ))}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </InfoCard>
              </Box>
            ) : null}
          </>
        ) : (
          <Paper className={classes.contentPaper} elevation={1}>
            <EmptyState
              missing="info"
              title="No story selected"
              description="Pick a story from the list to view its flow."
            />
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};
